import React from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import { Tiempos, NationalBook, NationalSemibold } from 'fonts';
import { GenericSectionContent, MediaCard, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SECTION_SPACING } from '../../../helpers/rendering';

/**
 * Atlas — the full-width statement banner: image behind, centred heading, body copy and CTAs over
 * it. Port of the legacy `homepage/why-caroma-banner` ("Experience Water" on the home page), and
 * structurally the same component Caroma ships, so the two storefronts stay in step.
 *
 * It used to be a second copy of Hero Banner: a fixed-height darkened image with the copy absolutely
 * positioned over its left edge. That is the wrong shape for this section -- the legacy banner
 * centres a narrow column of copy, and because the copy was taken out of flow it could overflow a
 * short banner or float in a tall one.
 *
 * Here the image is a background layer and the SECTION'S HEIGHT COMES FROM ITS COPY, with the legacy
 * heights kept as minimums so an image-only Atlas still looks like a banner. The Methven part is the
 * brand fonts, `brightness-75` (Caroma darkens to 50), and those minimums.
 */
export default function AtlasRendering({ content }: { content: GenericSectionContent }) {
  const { cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'AtlasRendering');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} relative w-full ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {cards.map((card: MediaCard, index: number) => {
          // An image-only Atlas (a plain banner) must not emit an empty heading block.
          const hasContent = Boolean(card?.title || card?.description || card?.ctas?.length);
          const isCenter = card?.content_alignment === 'Center';

          return (
            <div
              key={index}
              className="relative flex min-h-[52rem] w-full items-center justify-center overflow-hidden xl:min-h-[35rem] 2xl:min-h-[50rem]"
            >
              {card?.desktop_image?.url && (
                <Image
                  loading="lazy"
                  src={card.desktop_image.url}
                  alt={card.desktop_image.description ?? card?.title ?? ''}
                  fill
                  style={{ objectFit: 'cover' }}
                  className="brightness-75"
                />
              )}

              {hasContent && (
                <div className={`relative w-full px-24 py-60 lg:w-[47rem] lg:px-0 lg:py-80 2xl:py-120 ${isCenter ? 'text-center' : 'text-left'}`}>
                  {card?.title && (
                    <Typography as="h2" className={`text-32 font-semibold leading-10 text-white drop-shadow-sm 2xl:text-42 ${Tiempos.className}`}>
                      {card.title}
                    </Typography>
                  )}
                  {card?.description && (
                    <div
                      className={`${card?.title ? 'mt-24 lg:mt-32' : ''} whitespace-pre-line text-20 font-normal leading-7 text-white lg:leading-10 xl:text-18 2xl:text-20 ${NationalBook.className}`}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
                    />
                  )}
                  {card?.ctas && card.ctas.length > 0 && (
                    <div className={`${card?.title || card?.description ? 'mt-32 lg:mt-48' : ''} flex flex-wrap gap-16 ${isCenter ? 'justify-center' : 'justify-start'}`}>
                      {card.ctas.map((cta: Cta, ctaIndex: number) => (
                        <Link link={cta?.url} target={cta?.target ?? '_self'} key={ctaIndex} rel="noopener noreferrer">
                          <Button className="border-1 rounded-none border border-[#2B2C39] bg-white px-25 py-13 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4]">
                            <Typography as="span" className={`text-16 text-[#090909] xl:text-14 2xl:text-16 ${NationalSemibold.className}`}>
                              {cta?.label}
                            </Typography>
                          </Button>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
